import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";
import { redblueCommonValuesMessages } from "./common-values";
import { mergeLocaleCatalogSlices } from "./merge-message-tree";

const redblueCommonBaseMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      shell: {
        title: "红蓝对抗后台",
        portalEntry: "参演前台",
        loginPageTitle: "{centerTitle}登录",
      },
      menu: {
        members: "成员管理",
        schemes: "演练方案",
        activities: "演练活动",
        director: "导调台",
        judge: "裁判台",
        review: "态势与复盘",
        acceptance: "验收看板",
      },
      pages: {
        notFound: {
          title: "页面不存在",
          description: "红蓝对抗后台没有这个页面。",
        },
      },
      nouns: {
        activity: "演练活动",
        appRoles: "应用角色",
        camp: "阵营",
        campsSeats: "阵营与席位",
        directorPenalty: "导调扣分",
        member: "成员",
        reportTemplate: "报告模板",
        reportTemplates: "报告模板",
        scheme: "演练方案",
        schemeBasicInfo: "方案基础信息",
        schemeVersion: "方案版本",
        seat: "席位",
        stage: "阶段",
        subjectiveScore: "主观评分",
        task: "任务",
      },
      actions: {
        editScheme: "编辑方案",
        viewSchemeDetail: "查看方案",
        copyScheme: "复制为新草稿",
        archiveScheme: "归档方案",
        viewActivityDetail: "活动详情",
        backToActivities: "返回演练活动",
        backToSchemes: "返回演练方案",
        director: "导调台",
        judge: "裁判台",
        review: "态势复盘",
        enterDirector: "进入导调台",
        enterJudge: "进入裁判台",
        enterReview: "进入态势复盘",
        publishCurrentVersion: "发布当前版本",
        launchActivityFromPublishedVersion: "从发布版本发起演练",
      },
      columns: {
        actions: "操作",
        activityName: "演练名称",
        schemeName: "方案名称",
        mode: "模式",
        createMode: "创建方式",
        scheme: "方案",
        sourceScheme: "来源方案",
        sourceVersion: "来源版本",
        currentVersion: "当前版本",
        versionNo: "版本号",
        versionName: "版本名称",
        versionDescription: "版本描述",
        publishedBy: "发布人",
        publishedAt: "发布时间",
        camp: "阵营",
        topologyCamp: "拓扑角色",
        sortOrder: "排序",
        seat: "席位",
        roleHint: "角色提示",
        captainSeat: "队长席位",
        task: "任务",
        stage: "阶段",
        flowStatus: "流转状态",
        scoringStatus: "评分状态",
        target: "目标",
        member: "成员",
        userId: "用户 ID",
        activityRole: "演练角色",
        topology: "拓扑",
        topologyVersion: "拓扑版本",
        runtimeStatus: "运行状态",
        orchestrationRequest: "编排请求",
        topologyInstance: "拓扑实例",
        owner: "负责人",
        version: "版本",
        versionStatus: "版本状态",
        dispatchMode: "下发方式",
        dispatchTarget: "下发目标",
        rule: "规则",
        scoreKind: "评分类型",
        maxScore: "最高分",
        template: "模板名称",
        notice: "公告",
        report: "报告",
        reportType: "报告类型",
        taskId: "任务 ID",
        memberId: "成员 ID",
        dispatchedAt: "下发时间",
        submittedAt: "提交时间",
        reviewedAt: "复核时间",
        eventType: "事件类型",
        actorUserId: "操作者",
        subjectType: "对象类型",
        event: "事件",
        message: "消息",
        occurredAt: "发生时间",
        decidedBy: "裁定人",
        decidedAt: "裁定时间",
        scoreDelta: "分值变化",
        reason: "原因",
        targetRole: "目标角色",
        startedAt: "开始时间",
        endedAt: "结束时间",
      },
      messages: {
        activityScopeNotice: "任务、报告、评分和席位都归属具体演练活动。",
        missingSchemeDisplay: "缺少方案展示字段",
        missingVersionDisplay: "缺少版本展示字段",
        missingStructuredData: "缺少结构化数据",
        missingActivityDesign: "缺少活动设计快照",
        schemeCreated: "方案已创建",
        schemeVersionCreated: "方案版本已创建",
        schemeVersionCreateRequiresTopology: "请先绑定已发布拓扑版本，再创建方案版本。",
        schemeTopologySaved: "拓扑绑定已保存",
        schemeCampsSeatsSaved: "阵营与席位已保存",
        schemeStagesTasksSaved: "阶段与任务已保存",
        schemeReportTemplatesSaved: "报告模板已保存",
        schemeCopied: "方案已复制为新草稿",
        schemeArchived: "方案已归档",
        schemeUpdated: "方案已更新",
        activityCreated: "演练已创建",
        activityMembersSaved: "活动成员已保存",
        activityWizardCompleted: "演练创建流程已完成",
        schemeVersionPublished: "方案版本已发布",
        invalidSchemeVersionId: "请输入有效的发布方案版本 ID",
        invalidActivityId: "非法的演练 ID",
        invalidSchemeId: "非法的方案 ID",
        requestFailed: "请求失败",
        invalidPenaltyScore: "扣分值必须小于 0",
        missingRequiredField: "缺少必填字段：{field}",
        invalidFieldContract: "字段契约非法：{field}",
        missingEnumValue: "缺少 {field} 枚举值",
        unknownEnumValue: "{field} 枚举值非法：{value}",
        noBoundTopology: "当前演练未绑定拓扑实例。",
        membersSnapshotNotice: "成员配置使用后端成员快照；请在成员配置中维护身份与席位绑定。",
        schemeVersionPublishedSnapshotHint:
          "演练会从已发布方案版本快照生成执行态数据；未发布版本会被后端拒绝。",
        schemeEditOnlyDraft: "只有草稿方案可以编辑。",
      },
    },
  },
  en: {
    redblue: {
      shell: {
        title: "RedBlue Admin",
        portalEntry: "Participant Portal",
        loginPageTitle: "{centerTitle} Login",
      },
      menu: {
        members: "Members",
        schemes: "Exercise Schemes",
        activities: "Activities",
        director: "Director",
        judge: "Judge",
        review: "Review",
        acceptance: "Acceptance",
      },
      pages: {
        notFound: {
          title: "Page Not Found",
          description: "This RedBlue admin page does not exist.",
        },
      },
      nouns: {
        activity: "Exercise Activity",
        appRoles: "App Roles",
        camp: "Camp",
        campsSeats: "Camps and Seats",
        directorPenalty: "Director Penalty",
        member: "Member",
        reportTemplate: "Report Template",
        reportTemplates: "Report Templates",
        scheme: "Exercise Scheme",
        schemeBasicInfo: "Scheme Basics",
        schemeVersion: "Scheme Version",
        seat: "Seat",
        stage: "Stage",
        subjectiveScore: "Subjective Score",
        task: "Task",
      },
      actions: {
        editScheme: "Edit Scheme",
        viewSchemeDetail: "View Scheme",
        copyScheme: "Copy as Draft",
        archiveScheme: "Archive Scheme",
        viewActivityDetail: "Activity Detail",
        backToActivities: "Back to Activities",
        backToSchemes: "Back to Exercise Schemes",
        director: "Director",
        judge: "Judge",
        review: "Review",
        enterDirector: "Open Director",
        enterJudge: "Open Judge",
        enterReview: "Open Review",
        publishCurrentVersion: "Publish Current Version",
        launchActivityFromPublishedVersion: "Launch Activity from Published Version",
      },
      columns: {
        actions: "Actions",
        activityName: "Activity",
        schemeName: "Scheme",
        mode: "Mode",
        createMode: "Creation Mode",
        scheme: "Scheme",
        sourceScheme: "Source Scheme",
        sourceVersion: "Source Version",
        currentVersion: "Current Version",
        versionNo: "Version No.",
        versionName: "Version Name",
        versionDescription: "Version Description",
        publishedBy: "Published By",
        publishedAt: "Published At",
        camp: "Camp",
        topologyCamp: "Topology Role",
        sortOrder: "Sort Order",
        seat: "Seat",
        roleHint: "Role Hint",
        captainSeat: "Captain Seat",
        task: "Task",
        stage: "Stage",
        flowStatus: "Flow Status",
        scoringStatus: "Scoring Status",
        target: "Target",
        member: "Member",
        userId: "User ID",
        activityRole: "Activity Role",
        topology: "Topology",
        topologyVersion: "Topology Version",
        runtimeStatus: "Runtime Status",
        orchestrationRequest: "Orchestration Request",
        topologyInstance: "Topology Instance",
        owner: "Owner",
        version: "Version",
        versionStatus: "Version Status",
        dispatchMode: "Dispatch Mode",
        dispatchTarget: "Dispatch Target",
        rule: "Rule",
        scoreKind: "Score Type",
        maxScore: "Max Score",
        template: "Template Name",
        notice: "Announcement",
        report: "Report",
        reportType: "Report Type",
        taskId: "Task ID",
        memberId: "Member ID",
        dispatchedAt: "Dispatched At",
        submittedAt: "Submitted At",
        reviewedAt: "Reviewed At",
        eventType: "Event Type",
        actorUserId: "Actor User ID",
        subjectType: "Subject Type",
        event: "Event",
        message: "Message",
        occurredAt: "Occurred At",
        decidedBy: "Decided By",
        decidedAt: "Decided At",
        scoreDelta: "Score Delta",
        reason: "Reason",
        targetRole: "Target Role",
        startedAt: "Started At",
        endedAt: "Ended At",
      },
      messages: {
        activityScopeNotice: "Tasks, reports, scores, and seats all belong to a specific activity.",
        missingSchemeDisplay: "Missing scheme display fields",
        missingVersionDisplay: "Missing version display fields",
        missingStructuredData: "Missing structured data",
        missingActivityDesign: "Missing activity design snapshot",
        schemeCreated: "Scheme created",
        schemeVersionCreated: "Scheme version created",
        schemeVersionCreateRequiresTopology:
          "Bind a published topology version before creating scheme versions.",
        schemeTopologySaved: "Topology binding saved",
        schemeCampsSeatsSaved: "Camps and seats saved",
        schemeStagesTasksSaved: "Stages and tasks saved",
        schemeReportTemplatesSaved: "Report templates saved",
        schemeCopied: "Scheme copied as a new draft",
        schemeArchived: "Scheme archived",
        schemeUpdated: "Scheme updated",
        activityCreated: "Activity created",
        activityMembersSaved: "Activity members saved",
        activityWizardCompleted: "Activity creation flow completed",
        schemeVersionPublished: "Scheme version published",
        invalidSchemeVersionId: "Enter a valid published scheme version ID",
        invalidActivityId: "Invalid activity ID",
        invalidSchemeId: "Invalid scheme ID",
        requestFailed: "Request failed",
        invalidPenaltyScore: "Penalty score delta must be less than 0",
        missingRequiredField: "Missing required field: {field}",
        invalidFieldContract: "Invalid field contract: {field}",
        missingEnumValue: "Missing enum value for {field}",
        unknownEnumValue: "Invalid enum value for {field}: {value}",
        noBoundTopology: "This activity is not bound to a topology instance.",
        membersSnapshotNotice:
          "Member configuration uses backend snapshots. Maintain identity and seat bindings in member settings.",
        schemeVersionPublishedSnapshotHint:
          "Activities snapshot data from a published scheme version; unpublished versions will be rejected by the backend.",
        schemeEditOnlyDraft: "Only draft schemes can be edited.",
      },
    },
  },
  ja: {
    redblue: {
      shell: {
        title: "RedBlue管理",
        portalEntry: "参演ポータル",
        loginPageTitle: "{centerTitle} ログイン",
      },
      menu: {
        members: "メンバー管理",
        schemes: "演習プラン",
        activities: "演習一覧",
        director: "ディレクター",
        judge: "ジャッジ",
        review: "レビュー",
        acceptance: "受入ダッシュボード",
      },
      pages: {
        notFound: {
          title: "ページが見つかりません",
          description: "このRedBlue管理ページは存在しません。",
        },
      },
      nouns: {
        activity: "演習活動",
        appRoles: "アプリロール",
        camp: "陣営",
        campsSeats: "陣営と席位",
        directorPenalty: "ディレクター減点",
        member: "メンバー",
        reportTemplate: "レポートテンプレート",
        reportTemplates: "レポートテンプレート",
        scheme: "演習プラン",
        schemeBasicInfo: "プラン基本情報",
        schemeVersion: "プラン版",
        seat: "席位",
        stage: "段階",
        subjectiveScore: "主観採点",
        task: "タスク",
      },
      actions: {
        editScheme: "プランを編集",
        viewSchemeDetail: "プランを表示",
        copyScheme: "下書きとして複製",
        archiveScheme: "プランをアーカイブ",
        viewActivityDetail: "演習詳細",
        backToActivities: "演習一覧に戻る",
        backToSchemes: "演習プランに戻る",
        director: "ディレクター",
        judge: "ジャッジ",
        review: "レビュー",
        enterDirector: "ディレクターへ移動",
        enterJudge: "ジャッジへ移動",
        enterReview: "レビューへ移動",
        publishCurrentVersion: "現在の版を公開",
        launchActivityFromPublishedVersion: "公開済み版から演習を開始",
      },
      columns: {
        actions: "操作",
        activityName: "演習名",
        schemeName: "プラン名",
        mode: "モード",
        createMode: "作成方式",
        scheme: "プラン",
        sourceScheme: "元プラン",
        sourceVersion: "元バージョン",
        currentVersion: "現在の版",
        versionNo: "版番号",
        versionName: "版名",
        versionDescription: "版の説明",
        publishedBy: "公開者",
        publishedAt: "公開日時",
        camp: "陣営",
        topologyCamp: "トポロジーロール",
        sortOrder: "並び順",
        seat: "席位",
        roleHint: "役割ヒント",
        captainSeat: "隊長席位",
        task: "タスク",
        stage: "段階",
        flowStatus: "進行状態",
        scoringStatus: "採点状態",
        target: "対象",
        member: "メンバー",
        userId: "ユーザー ID",
        activityRole: "演習ロール",
        topology: "トポロジー",
        topologyVersion: "トポロジー版",
        runtimeStatus: "実行状態",
        orchestrationRequest: "編成要求",
        topologyInstance: "トポロジー実体",
        owner: "担当者",
        version: "版",
        versionStatus: "版状態",
        dispatchMode: "配布方式",
        dispatchTarget: "配布対象",
        rule: "ルール",
        scoreKind: "採点種別",
        maxScore: "満点",
        template: "テンプレート名",
        notice: "告知",
        report: "レポート",
        reportType: "レポート種別",
        taskId: "タスク ID",
        memberId: "メンバー ID",
        dispatchedAt: "配布日時",
        submittedAt: "提出日時",
        reviewedAt: "レビュー日時",
        eventType: "イベント種別",
        actorUserId: "操作ユーザー",
        subjectType: "対象種別",
        event: "イベント",
        message: "メッセージ",
        occurredAt: "発生日時",
        decidedBy: "裁定者",
        decidedAt: "裁定日時",
        scoreDelta: "点数差分",
        reason: "理由",
        targetRole: "対象ロール",
        startedAt: "開始日時",
        endedAt: "終了日時",
      },
      messages: {
        activityScopeNotice: "タスク、レポート、採点、席位はすべて特定の演習活動に属します。",
        missingSchemeDisplay: "プラン表示用フィールドが不足しています",
        missingVersionDisplay: "版表示用フィールドが不足しています",
        missingStructuredData: "構造化データが不足しています",
        missingActivityDesign: "演習設計スナップショットが不足しています",
        schemeCreated: "プランを作成しました",
        schemeVersionCreated: "プラン版を作成しました",
        schemeVersionCreateRequiresTopology:
          "プラン版を作成する前に、公開済みトポロジー版を紐付けてください。",
        schemeTopologySaved: "トポロジー紐付けを保存しました",
        schemeCampsSeatsSaved: "陣営と席位を保存しました",
        schemeStagesTasksSaved: "段階とタスクを保存しました",
        schemeReportTemplatesSaved: "レポートテンプレートを保存しました",
        schemeCopied: "プランを新しい下書きとして複製しました",
        schemeArchived: "プランをアーカイブしました",
        schemeUpdated: "プランを更新しました",
        activityCreated: "演習を作成しました",
        activityMembersSaved: "演習メンバーを保存しました",
        activityWizardCompleted: "演習作成フローが完了しました",
        schemeVersionPublished: "プラン版を公開しました",
        invalidSchemeVersionId: "有効な公開済みプラン版 ID を入力してください",
        invalidActivityId: "不正な演習 ID です",
        invalidSchemeId: "不正なプラン ID です",
        requestFailed: "リクエストに失敗しました",
        invalidPenaltyScore: "減点値は 0 より小さくしてください",
        missingRequiredField: "必須フィールドが不足しています: {field}",
        invalidFieldContract: "フィールド契約が不正です: {field}",
        missingEnumValue: "{field} の列挙値が不足しています",
        unknownEnumValue: "{field} の列挙値が不正です: {value}",
        noBoundTopology: "この演習にはトポロジー実体が紐付いていません。",
        membersSnapshotNotice:
          "メンバー構成はバックエンドのスナップショットを使用します。ID と席位の紐付けはメンバー設定で管理してください。",
        schemeVersionPublishedSnapshotHint:
          "演習は公開済みプラン版のスナップショットから生成されます。未公開版はバックエンドで拒否されます。",
        schemeEditOnlyDraft: "編集できるのは下書きプランのみです。",
      },
    },
  },
};

export const redblueCommonMessages: Record<AppLocale, MessageTree> = mergeLocaleCatalogSlices([
  redblueCommonBaseMessages,
  redblueCommonValuesMessages,
]);
